import { DriverDto, RouteDto, TruckDto } from '@trucktrax/trucktrax-ts-common';

import { RouteTruckDetailsDto } from '../types';

/**
 * Which trucks/drivers a route history search should include.
 * `internal`/`external` select on ownership (an external truck or driver hauls
 * for us but isn't ours); `deactivated` additionally admits archived records.
 */
export interface OwnershipFilter {
  internal: boolean;
  external: boolean;
  deactivated: boolean;
}

/**
 * Default filter state. Internal and external are both on so that the initial
 * search returns the same routes it did before ownership filtering existed;
 * deactivated stays off, matching the switch it replaced.
 */
export const DEFAULT_OWNERSHIP_FILTER: OwnershipFilter = {
  internal: true,
  external: true,
  deactivated: false,
};

/**
 * Whether a record's ownership is admitted by the filter. Anything not
 * explicitly flagged external is treated as internal, matching how the rest of
 * the app reads `isExternal`.
 * @param {boolean | undefined} isExternal the record's external flag
 * @param {OwnershipFilter} filter the active ownership filter
 * @returns {boolean} true when the record's ownership is selected
 */
export function matchesOwnership(
  isExternal: boolean | undefined,
  filter: OwnershipFilter,
): boolean {
  return isExternal === true ? filter.external : filter.internal;
}

/**
 * Narrows a truck list to the selected ownership, dropping archived trucks
 * unless deactivated trucks were requested.
 * @param {TruckDto[] | undefined} truckList the full truck list, before it has loaded
 * @param {OwnershipFilter} filter the active ownership filter
 * @returns {TruckDto[]} the trucks to offer for selection
 */
export function filterTrucksByOwnership(
  truckList: TruckDto[] | undefined,
  filter: OwnershipFilter,
): TruckDto[] {
  return (truckList ?? []).filter(
    truck =>
      matchesOwnership(truck.isExternal, filter) &&
      (filter.deactivated || truck.archived === false),
  );
}

/**
 * Narrows a driver list to the selected ownership, dropping archived drivers
 * unless deactivated drivers were requested.
 * @param {DriverDto[] | undefined} driverList the full driver list, before it has loaded
 * @param {OwnershipFilter} filter the active ownership filter
 * @returns {DriverDto[]} the drivers to offer for selection
 */
export function filterDriversByOwnership(
  driverList: DriverDto[] | undefined,
  filter: OwnershipFilter,
): DriverDto[] {
  return (driverList ?? []).filter(
    driver =>
      matchesOwnership(driver.isExternal, filter) &&
      (filter.deactivated || driver.archived === false),
  );
}

/**
 * Stamps each route's truck with its driver's ownership, resolved by matching
 * `truck.driverUrl` against drivers already loaded for the search. `/routehistory`
 * reports the truck's ownership but not the driver's, so this fills the gap
 * without a backend change. A driver missing from the list (for instance one
 * outside the selected product lines) is left undefined and treated as internal.
 * @param {RouteDto[] | undefined} routes the fetched routes
 * @param {DriverDto[] | undefined} driverList drivers loaded by the search form
 * @returns {RouteDto[]} the same routes, with driverIsExternal populated where known
 */
export function annotateDriverOwnership(
  routes: RouteDto[] | undefined,
  driverList: DriverDto[] | undefined,
): RouteDto[] {
  const externalByUrl = new Map<string, boolean>();
  (driverList ?? []).forEach(driver => {
    if (driver.url) {
      externalByUrl.set(driver.url, driver.isExternal === true);
    }
  });

  const annotated = routes ?? [];
  annotated.forEach(route => {
    const truck = route.truck as RouteTruckDetailsDto | undefined;
    if (truck?.driverUrl !== undefined) {
      truck.driverIsExternal = externalByUrl.get(truck.driverUrl);
    }
  });

  return annotated;
}

/**
 * Drops routes whose truck or driver ownership the user didn't select. Deactivated
 * records are still excluded by the backend's hideDeactivated* params, so only the
 * internal/external part of each filter is applied here.
 * @param {RouteDto[] | undefined} routes the fetched routes
 * @param {OwnershipFilter | undefined} truckOwnership truck selection
 * @param {OwnershipFilter | undefined} driverOwnership driver selection
 * @returns {RouteDto[]} routes the user asked to see
 */
export function filterRoutesByOwnership(
  routes: RouteDto[] | undefined,
  truckOwnership: OwnershipFilter | undefined,
  driverOwnership: OwnershipFilter | undefined,
): RouteDto[] {
  if (!truckOwnership && !driverOwnership) {
    return routes ?? [];
  }
  return (routes ?? []).filter(route => {
    const truck = route.truck as RouteTruckDetailsDto | undefined;
    const truckOk = !truckOwnership || matchesOwnership(truck?.isExternal, truckOwnership);
    const driverOk = !driverOwnership || matchesOwnership(truck?.driverIsExternal, driverOwnership);
    return truckOk && driverOk;
  });
}

/**
 * Which route-status workflows a set of results actually exercises, so the legend
 * only advertises statuses those routes can reach.
 *
 * Keyed on the **driver's** ownership, not the truck's: a status workflow is a
 * property of who is driving (see `statusPermittedByAssociatedWorkflow`, which
 * takes `position.driver.isExternal`, and the INTERNAL/EXTERNAL_DRIVER_ROUTES
 * workflow sources in statusesUtil). An external driver in one of our trucks
 * reports the external workflow, and vice versa.
 *
 * Reads `driverIsExternal`, which annotateDriverOwnership must have populated
 * first. A driver it could not resolve stays undefined and counts as internal.
 * @param {RouteDto[] | null | undefined} routeHistoryList the current search results
 * @returns {{ includeInternal: boolean; includeExternal: boolean } | undefined} workflows present
 */
export function getRoutesOwnership(
  routeHistoryList: RouteDto[] | null | undefined,
): { includeInternal: boolean; includeExternal: boolean } | undefined {
  const routes = routeHistoryList ?? [];
  if (!routes.length) {
    return undefined;
  }
  const driverIsExternal = (route: RouteDto) =>
    (route.truck as RouteTruckDetailsDto | undefined)?.driverIsExternal;
  return {
    includeInternal: routes.some(r => driverIsExternal(r) !== true),
    includeExternal: routes.some(r => driverIsExternal(r) === true),
  };
}

/**
 * Returns the product lines list from the route history search result
 * @param {RouteDto[]} routeHistoryListResult route history list result
 * @param {number} validProductLinesLength valid product lines length
 * @returns {string[]} product lines from the result list
 */

export function getProductLinesResult(
  routeHistoryListResult: RouteDto[],
  validProductLinesLength: number,
): string[] {
  const routeProductLines: string[] = [];
  if (routeHistoryListResult === undefined || routeHistoryListResult.length === 0)
    return routeProductLines;
  for (let index = 0; index < routeHistoryListResult.length; ) {
    if (routeHistoryListResult[index].route !== undefined) {
      const routeProductLine = routeHistoryListResult[index].route[0].productLine;
      if (routeProductLines.length === validProductLinesLength) break;
      if (!routeProductLines.includes(routeProductLine)) {
        routeProductLines.push(routeProductLine);
      }
    }
    index += 1;
  }

  return routeProductLines;
}

/**
 * Returns the text indicating one or more deactivated resources on a route history search result
 * @param {any} deactivatedTruck indicates if there's a deactivated truck
 * @param {any} deactivatedDriver indicates if there's a deactivated driver
 * @returns {string} text for deactivated resource(s)
 */

export function getDeactivationType(deactivatedTruck: any, deactivatedDriver: any): string {
  if (!deactivatedTruck && !deactivatedDriver) {
    return '';
  }
  let deactivatedType = '';
  if (deactivatedTruck && deactivatedDriver) {
    deactivatedType = 'Truck and Driver';
  } else if (deactivatedTruck) {
    deactivatedType = 'Truck';
  } else {
    deactivatedType = 'Driver';
  }
  return `Deactivated ${deactivatedType}`;
}

/**
 * Returns the text indicating one or more external resources on a route history
 * search result, so external hauls are distinguishable from our own.
 * @param {boolean | undefined} externalTruck indicates if the truck is external
 * @param {boolean | undefined} externalDriver indicates if the driver is external
 * @returns {string} text for external resource(s)
 */
export function getExternalType(
  externalTruck: boolean | undefined,
  externalDriver: boolean | undefined,
): string {
  if (!externalTruck && !externalDriver) {
    return '';
  }
  let externalType = '';
  if (externalTruck && externalDriver) {
    externalType = 'Truck and Driver';
  } else if (externalTruck) {
    externalType = 'Truck';
  } else {
    externalType = 'Driver';
  }
  return `External ${externalType}`;
}
